// src/components/dashboard/Dashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./dashboard.css";
import { AppRepoProvider } from "../../repos/AppRepoProvider";
import expertLogo from "../../assets/expert_logo.webp";
import Advance from "../advance/Advance";
import Deduction from "../deduction/Deduction";
import Invoices from "../invoices/Invoices";
import RateChart from "../rates/Ratechart";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell, // ✅ add this
} from "recharts";


import { api } from "../../services/api";

import {
  fetchKendraList,
  loadSelectedKendra,
  saveSelectedKendra,
} from "../../services/kendra";

import { clearSession, getSession } from "../../services/auth";
import { useNavigate } from "react-router-dom";

// Pages
import Collections from "../collections/Collections";
import Farmers from "../farmers/Farmers";
import Foods from "../foods/Foods";

function Icon({ children }) {
  return (
    <span className="mdIcon" aria-hidden="true">
      {children}
    </span>
  );
}

/** Simple center picker modal */
function KendraPicker({ open, onClose, list, selected, onPick, errorMsg }) {
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  if (!open) return null;

  const filtered = list.filter((k) => {
    const s = `${k.sr} ${k.name} ${k.mobno} ${k.city} ${k.type} ${k.status} ${k.mainuser}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });

  return (
    <div className="mdModalOverlay" role="dialog" aria-modal="true">
      <div className="mdModalCard">
        <div className="mdModalHead">
          <div className="mdModalTitle">Select center</div>
          <button className="mdModalClose" type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="mdModalBody">
          <div className="mdModalFilters">
            <div className="mdFilterBlock">
              <div className="mdFilterLabel">Center Type</div>
              <select className="mdFilterSelect" defaultValue="All" disabled>
                <option>All</option>
              </select>
            </div>

            <div className="mdFilterBlock right">
              <div className="mdFilterLabel">Search</div>
              <input
                className="mdFilterSearch"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>

          <div className="mdTableWrap">
            <div className="mdTableTopRow">
              <div className="mdTableTitle">Centers</div>
            </div>

            {errorMsg ? (
              <div className="mdTableMsg error">{errorMsg}</div>
            ) : filtered.length === 0 ? (
              <div className="mdTableMsg">Showing 1 to 0 of 0 entries</div>
            ) : (
              <table className="mdTable">
                <thead>
                  <tr>
                    <th>sr</th>
                    <th>name</th>
                    <th>mobno</th>
                    <th>city</th>
                    <th>type</th>
                    <th>status</th>
                    <th>mainuser</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((k) => {
                    const isSel = selected && String(selected.sr) === String(k.sr);
                    return (
                      <tr
                        key={String(k.sr)}
                        className={isSel ? "selected" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => onPick(k)}
                      >
                        <td>{k.sr}</td>
                        <td>{k.name}</td>
                        <td>{k.mobno}</td>
                        <td>{k.city}</td>
                        <td>{k.type}</td>
                        <td>{k.status}</td>
                        <td>{k.mainuser}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            <div className="mdTableFooter">
              Showing 1 to {filtered.length} of {filtered.length} entries
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Click-outside helper */
function useClickOutside(ref, handler, when = true) {
  useEffect(() => {
    if (!when) return;

    const onDown = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) handler();
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [ref, handler, when]);
}

/** Custom tooltip for modern look */
function SanklanTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="mdChartTip">
      <div className="mdChartTipTitle">{label}</div>
      <div className="mdChartTipLine">Amount: ₹ {row?.amt_total ?? 0}</div>
      <div className="mdChartTipLine">Liter: {row?.liter_total ?? 0}</div>
      <div className="mdChartTipLine">Entries: {row?.entry_count ?? 0}</div>
    </div>
  );
}

/** Dashboard home (graph + cache + ETag revalidate) */
function DashboardHome({ mainuser, selectedKendra }) {
  const kendramobno = selectedKendra?.mobno ? String(selectedKendra.mobno) : "";

  const cacheKey = useMemo(() => {
    return `dash_sanklan_10_${mainuser || "x"}_${kendramobno || "all"}`;
  }, [mainuser, kendramobno]);

  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [err, setErr] = useState("");
  const [etag, setEtag] = useState("");
  const [days, setDays] = useState([]);
  const [totals, setTotals] = useState({ liter_total: 0, amt_total: 0, entry_count: 0 });
  const [metric, setMetric] = useState("amt_total"); // amt_total | liter_total

  // 1) show cached immediately
  useEffect(() => {
    setErr("");
    setLoading(true);
    setStale(false);

    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const j = JSON.parse(raw);
        if (j?.days) setDays(j.days);
        if (j?.totals) setTotals(j.totals);
        if (j?.etag) setEtag(j.etag);
        setLoading(false);
        setStale(true); // we will revalidate in background
      }
    } catch {
      // ignore cache errors
    }
  }, [cacheKey]);

  // 2) revalidate with ETag
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!mainuser) return;

      setErr("");
      // keep chart visible (if cache shown), only show loading if no cache
      if (!days || days.length === 0) setLoading(true);

      try {
        const res = await api.get("get_sanklan_10days_summary.php", {
          params: { mainuser, kendramobno },
          headers: etag ? { "If-None-Match": etag } : undefined,
          validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
        });

        if (cancelled) return;

        if (res.status === 304) {
          // unchanged, keep cached data
          setStale(false);
          setLoading(false);
          return;
        }

        const newEtag = res.headers?.etag || "";
        const data = res.data;

        if (data?.ok) {
          setDays(data.days || []);
          setTotals(data.totals || { liter_total: 0, amt_total: 0, entry_count: 0 });
          setEtag(newEtag);
          setStale(false);
          setLoading(false);

          // save offline cache
          try {
            localStorage.setItem(
              cacheKey,
              JSON.stringify({
                etag: newEtag,
                savedAt: Date.now(),
                totals: data.totals,
                days: data.days,
              })
            );
          } catch {
            // ignore storage failures
          }
        } else {
          setErr(data?.error || "Failed to load dashboard data");
          setLoading(false);
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || "Network Error");
        setLoading(false);
        // keep cached data visible
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // IMPORTANT: include etag so it revalidates correctly per center
  }, [mainuser, kendramobno, cacheKey, etag]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = useMemo(() => {
    return (days || []).map((d) => ({
      ...d,
      label: d.dmy || d.date,
    }));
  }, [days]);

  const avg = useMemo(() => {
    const arr = (chartData || [])
      .map((x) => Number(x?.[metric] || 0))
      .filter((n) => Number.isFinite(n));

    if (arr.length === 0) return 0;
    const sum = arr.reduce((a, b) => a + b, 0);
    return sum / arr.length;
  }, [chartData, metric]);

  // middle band = ±5% of avg (yellow). Adjust 0.05 -> 0.08 etc if you want.
  const band = useMemo(() => {
    if (!avg) return 0;
    return Math.abs(avg) * 0.05;
  }, [avg]);

  function barColor(v) {
    const n = Number(v || 0);
    if (!Number.isFinite(n) || avg === 0) return "#9CA3AF"; // gray if no data
    if (Math.abs(n - avg) <= band) return "#F59E0B"; // ✅ yellow (near average)
    if (n > avg) return "#22C55E"; // ✅ green (above average)
    return "#EF4444"; // ✅ red (below average)
  }


  const metricLabel = metric === "liter_total" ? "Liters" : "Amount (₹)";

  return (
    <div className="mdDashGrid">
      <div className="mdDashCard">
        <div className="mdDashHead">
          <div>
            <div className="mdDashTitle">Last 10 Days Collection</div>
            <div className="mdDashSub">
              {selectedKendra?.name ? selectedKendra.name : "All Centers"} • {metricLabel}
              {stale ? <span className="mdDashPill">Updating…</span> : null}
            </div>
          </div>

          <div className="mdDashActions">
            <button
              type="button"
              className={`mdDashChip ${metric === "amt_total" ? "active" : ""}`}
              onClick={() => setMetric("amt_total")}
            >
              Amount
            </button>
            <button
              type="button"
              className={`mdDashChip ${metric === "liter_total" ? "active" : ""}`}
              onClick={() => setMetric("liter_total")}
            >
              Liter
            </button>
          </div>
        </div>

        <div className="mdDashStats">
          <div className="mdStat">
            <div className="mdStatLabel">Total Amount</div>
            <div className="mdStatValue">₹ {Number(totals?.amt_total || 0).toFixed(2)}</div>
          </div>
          <div className="mdStat">
            <div className="mdStatLabel">Total Liter</div>
            <div className="mdStatValue">{Number(totals?.liter_total || 0).toFixed(2)}</div>
          </div>
          <div className="mdStat">
            <div className="mdStatLabel">Entries</div>
            <div className="mdStatValue">{Number(totals?.entry_count || 0)}</div>
          </div>
        </div>

        {err ? <div className="mdDashError">{err}</div> : null}

        <div className="mdDashChartWrap">
          {loading && (!chartData || chartData.length === 0) ? (
            <div className="mdDashSkeleton">Loading chart…</div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 10, right: 18, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tickMargin={8} />
                <YAxis tickMargin={8} />
                <Tooltip content={<SanklanTooltip />} />
                <Bar dataKey={metric} radius={[10, 10, 0, 0]}>
                  {(chartData || []).map((row, i) => (
                    <Cell key={`cell-${i}`} fill={barColor(row?.[metric])} />
                  ))}
                </Bar>

              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openMilkSale, setOpenMilkSale] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  // active page
  const [activePage, setActivePage] = useState("dashboard");

  // center picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [kList, setKList] = useState([]);
  const [kError, setKError] = useState("");
  const [selected, setSelected] = useState(loadSelectedKendra());

  // profile dropdown
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  useClickOutside(profileRef, () => setProfileOpen(false), profileOpen);

  const session = getSession();
  const mainuser = session?.mobno || session?.user?.mobno || session?.user?.username || "";

  // load kendra list once
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!mainuser) return;

      setKError("");
      try {
        const list = await fetchKendraList(mainuser);
        if (cancelled) return;

        setKList(list);

        const stored = loadSelectedKendra();
        const matchStored = stored && list.find((x) => String(x.sr) === String(stored.sr));

        let pick = null;
        if (matchStored) pick = matchStored;
        else if (list.length === 1) pick = list[0];
        else if (list.length > 0) pick = list[0];

        if (pick) {
          setSelected(pick);
          saveSelectedKendra(pick);
        }
      } catch (e) {
        if (cancelled) return;
        setKError(e?.message || "Network Error");
        setKList([]);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [mainuser]);

  const headerTitle = useMemo(() => (selected?.name ? selected.name : "Expert Dairy"), [selected]);

  const profileName = useMemo(() => {
    const n = (session?.user?.enname || session?.user?.dairy || "User").trim();
    return n || "User";
  }, [session]);

  const profileMobile = useMemo(() => session?.user?.mobno || session?.mobno || "", [session]);

  const avatarLetter = useMemo(() => {
    const n = (session?.user?.enname || session?.user?.dairy || session?.user?.mobno || "E").trim();
    return (n[0] || "E").toUpperCase();
  }, [session]);

  function doLogout() {
    const ok = window.confirm("Logout?");
    if (!ok) return;

    clearSession();
    localStorage.removeItem("selected_kendra");
    setProfileOpen(false);
    navigate("/", { replace: true });
  }

  async function refreshProfile() {
    setKError("");
    try {
      const list = await fetchKendraList(mainuser);
      setKList(list);

      const stored = loadSelectedKendra();
      const matchStored = stored && list.find((x) => String(x.sr) === String(stored.sr));
      const pick = matchStored || (list.length ? list[0] : null);

      if (pick) {
        setSelected(pick);
        saveSelectedKendra(pick);
      }
    } catch (e) {
      setKError(e?.message || "Network Error");
      setKList([]);
    } finally {
      setProfileOpen(false);
    }
  }

  // breadcrumb
  const breadcrumb = useMemo(() => {
    if (activePage === "collections") return { icon: "▣", text: "Collections" };
    if (activePage === "farmers") return { icon: "👤", text: "Farmers" };
    if (activePage === "foods") return { icon: "📖", text: "Foods" };
    if (activePage === "advance") return { icon: "💳", text: "Advance" };
    if (activePage === "deduction") return { icon: "✂", text: "Deduction" };
    if (activePage === "invoices") return { icon: "🧾", text: "Invoice" };
    if (activePage === "ratechart") return { icon: "📊", text: "Rate Chart" };
    return { icon: "▦", text: "Dashboard" };
  }, [activePage]);

  return (
    <AppRepoProvider mainuser={mainuser}>
      <div className="mdShell">
        {/* Sidebar */}
        <aside className={`mdSidebar ${sidebarOpen ? "open" : "collapsed"}`}>
          <div className="mdSideTop">
            <button
              className="mdBurger"
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label="Toggle sidebar"
              title="Menu"
            >
              ☰
            </button>

            <div className="mdBrand">
              <img className="mdLogo" src={expertLogo} alt="Expert Dairy" />
            </div>
          </div>

          <nav className="mdNav">
            <button
              className={`mdNavItem ${activePage === "dashboard" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("dashboard")}
            >
              <Icon>▦</Icon>
              <span className="mdNavText">Dashboard</span>
            </button>

            <button
              className={`mdNavItem ${activePage === "collections" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("collections")}
            >
              <Icon>▣</Icon>
              <span className="mdNavText">Collections</span>
            </button>

            <button
              className={`mdNavItem ${activePage === "farmers" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("farmers")}
            >
              <Icon>👤</Icon>
              <span className="mdNavText">Farmers</span>
            </button>

            <button
              className={`mdNavItem ${activePage === "foods" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("foods")}
            >
              <Icon>📖</Icon>
              <span className="mdNavText">Foods</span>
            </button>

            <button
              className={`mdNavItem ${activePage === "advance" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("advance")}
            >
              <Icon>💳</Icon>
              <span className="mdNavText">Advance</span>
            </button>

            <button
              className={`mdNavItem ${activePage === "deduction" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("deduction")}
            >
              <Icon>✂</Icon>
              <span className="mdNavText">Deduction</span>
            </button>

            <button
              className={`mdNavItem ${activePage === "invoices" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("invoices")}
            >
              <Icon>🧾</Icon>
              <span className="mdNavText">Invoices</span>
            </button>

            <button
              className={`mdNavItem ${activePage === "ratechart" ? "active" : ""}`}
              type="button"
              onClick={() => setActivePage("ratechart")}
            >
              <Icon>📊</Icon>
              <span className="mdNavText">Rate Chart</span>
            </button>

            {/* <button className="mdNavItem" type="button">
              <Icon>📅</Icon>
              <span className="mdNavText">Annual Bonus</span>
            </button>

            <button
              className={`mdNavItem ${openMilkSale ? "open" : ""}`}
              type="button"
              onClick={() => setOpenMilkSale((v) => !v)}
            >
              <Icon>🥛</Icon>
              <span className="mdNavText">Milk Sale</span>
              <span className="mdChevron">{openMilkSale ? "▾" : "▸"}</span>
            </button>

            <div className={`mdSub ${openMilkSale ? "show" : ""}`}>
              <button className="mdSubItem" type="button">Sale Entry</button>
              <button className="mdSubItem" type="button">Sale Report</button>
            </div> */}

            {/* <button className="mdNavItem" type="button">
              <Icon>📁</Icon>
              <span className="mdNavText">Reports</span>
            </button>

            <button
              className={`mdNavItem ${openSettings ? "open" : ""}`}
              type="button"
              onClick={() => setOpenSettings((v) => !v)}
            >
              <Icon>⚙</Icon>
              <span className="mdNavText">Settings</span>
              <span className="mdChevron">{openSettings ? "▾" : "▸"}</span>
            </button>

            <div className={`mdSub ${openSettings ? "show" : ""}`}>
              <button className="mdSubItem" type="button">Profile</button>
              <button className="mdSubItem" type="button">Center Settings</button>
            </div> */}
          </nav>

          <div className="mdSideBottom">
            <button className="mdLogout" type="button" title="Logout" onClick={doLogout}>
              🔒
            </button>

            <div className="mdFooter">
              <div className="mdFooterLine" />
              <div className="mdFooterText">EXPERT DAIRY© 2025</div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="mdMain">
          <header className="mdTopbar">
            <div className="mdTopLeft" />

            <div className="mdTopCenter">
              <button
                className="mdCenterBtn"
                type="button"
                onClick={() => setPickerOpen(true)}
                title="Select center"
              >
                <span className="mdCenterText">{headerTitle}</span>
                <span className="mdCenterChevron">▾</span>
              </button>
            </div>

            <div className="mdTopRight" ref={profileRef} style={{ position: "relative" }}>
              <button
                className="mdAvatar"
                type="button"
                title="Profile"
                onClick={() => setProfileOpen((v) => !v)}
              >
                {avatarLetter}
              </button>

              {profileOpen && (
                <div className="mdProfileMenu">
                  <div className="mdProfileHead">
                    <div className="mdProfileAvatar">{avatarLetter}</div>
                    <div className="mdProfileHeadText">
                      <div className="mdProfileName">{profileName}</div>
                      <div className="mdProfileMobile">{profileMobile}</div>
                    </div>
                  </div>

                  <div className="mdProfileSectionTitle">Profile</div>

                  <button className="mdProfileItem" type="button" onClick={refreshProfile}>
                    <span className="mdProfileItemIcon">⟳</span>
                    <span>Refresh Profile</span>
                  </button>

                  <button className="mdProfileItem" type="button" onClick={doLogout}>
                    <span className="mdProfileItemIcon">⏻</span>
                    <span>Logout</span>
                  </button>

                  <div className="mdProfileSectionTitle">Organisations</div>

                  {(kList || []).map((k) => {
                    const isSel = selected && String(selected.sr) === String(k.sr);
                    return (
                      <button
                        key={String(k.sr)}
                        className={`mdOrgItem ${isSel ? "active" : ""}`}
                        type="button"
                        onClick={() => {
                          setSelected(k);
                          saveSelectedKendra(k);
                          setProfileOpen(false);
                        }}
                      >
                        <span className="mdOrgTick">{isSel ? "✔" : ""}</span>
                        <span className="mdOrgName">{k.name}</span>
                      </button>
                    );
                  })}

                  {kError ? <div className="mdProfileError">{kError}</div> : null}
                </div>
              )}
            </div>
          </header>

          <div className="mdContentHeader">
            <div className="mdBreadcrumb">
              <span className="mdBreadcrumbIcon">{breadcrumb.icon}</span>
              <span className="mdBreadcrumbText">{breadcrumb.text}</span>
            </div>
          </div>

          <div className="mdContent">
            {activePage === "collections" ? (
              <Collections mainuser={mainuser} selectedKendra={selected} />
            ) : activePage === "farmers" ? (
              <Farmers />
            ) : activePage === "foods" ? (
              <Foods mainuser={mainuser} />
            ) : activePage === "advance" ? (
              <Advance mainuser={mainuser} selectedKendra={selected} />
            ) : activePage === "deduction" ? (
              <Deduction mainuser={mainuser} selectedKendra={selected} />
            ) : activePage === "invoices" ? (
              <Invoices mainuser={mainuser} selectedKendra={selected} />
            ) : activePage === "ratechart" ? (
              <RateChart mainuser={mainuser} selectedKendra={selected} />
            ) : (
              // ✅ DASHBOARD GRAPH HERE
              <DashboardHome mainuser={mainuser} selectedKendra={selected} />
            )}
          </div>
        </main>

        <KendraPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          list={kList}
          selected={selected}
          errorMsg={kError}
          onPick={(k) => {
            setSelected(k);
            saveSelectedKendra(k);
            setPickerOpen(false);
          }}
        />
      </div>
    </AppRepoProvider>
  );
}
